import * as Sentry from '@sentry/react'
import Cookies from 'js-cookie'
import type { FetchResponse, MaybeOptionalInit } from 'openapi-fetch'
import createOpenApiClient from 'openapi-fetch'
import type { MediaType, PathsWithMethod, RequiredKeysOf } from 'openapi-typescript-helpers'

import { ApiError, parseErrors } from './api-error'
import { paramsSerializer } from './params-serializer'
import type { AllPaths } from './paths'

type ApiHandledResponse<TData> = {
    data: TData
} & Pick<Response, 'headers' | 'status' | 'statusText'>

type ExtractData<T> = T extends { data: infer TData } ? TData : never

// Helper type for method response - used for type assertions in wrapped methods
type MethodHandledResponse<TData> = Promise<ApiHandledResponse<ExtractData<Awaited<TData>>>>

const csrftoken = Cookies.get('csrftoken')

const client = createOpenApiClient<AllPaths>({
    querySerializer: paramsSerializer,
    credentials: 'include',
    headers: csrftoken ? { 'X-CSRFToken': csrftoken } : undefined,
    baseUrl: process.env.NODE_ENV === 'test' ? 'http://localhost:9090' : undefined,
})

// Helper type to extract the init parameter type
type InitParam<TInit> = RequiredKeysOf<TInit> extends never
    ? [(TInit & Record<string, unknown>)?]
    : [TInit & Record<string, unknown>]

// Helper to add Sentry headers to options
function addSentryHeaders<T>(options: T | undefined): T {
    const currentSpan = Sentry.getActiveSpan() ?? Sentry.startInactiveSpan({ name: 'Manual Span' })
    const sentryTraceHeader = Sentry.spanToTraceHeader(currentSpan)
    const sentryBaggageHeader = Sentry.spanToBaggageHeader(currentSpan)

    const baseOptions = (options ?? {}) as Record<string, unknown>

    return {
        ...baseOptions,
        headers: {
            ...(baseOptions.headers as Record<string, unknown> | undefined),
            ...(sentryTraceHeader ? { 'sentry-trace': sentryTraceHeader } : {}),
            ...(sentryBaggageHeader ? { baggage: sentryBaggageHeader } : {}),
        },
    } as T
}

// Helper to handle API response
function handleApiResponse<T>(
    result: { data: T; error?: never; response: Response } | { data?: never; error: unknown; response: Response },
): ApiHandledResponse<T> {
    if ('error' in result && result.error !== undefined) {
        const { message, formErrors } = parseErrors(result.error)

        throw new ApiError({
            status: result.response.status,
            message: message ?? result.response.statusText,
            formErrors,
            originalError: result.error,
        })
    }

    return {
        data: result.data as T,
        headers: result.response.headers,
        status: result.response.status,
        statusText: result.response.statusText,
    }
}

// Wrapper to handle errors and add Sentry headers
async function withErrorHandling<T>(
    fn: () => Promise<{ data: T; error?: never; response: Response } | { data?: never; error: unknown; response: Response }>,
): Promise<ApiHandledResponse<T>> {
    try {
        const result = await fn()

        return handleApiResponse(result)
    } catch (error) {
        if (error instanceof ApiError) throw error
        throw new ApiError({
            status: 500,
            message: error instanceof Error ? error.message : 'Something went wrong',
            originalError: error instanceof Error ? error : undefined,
        })
    }
}

// Wrap each method with error handling and Sentry headers
function wrappedGet<
    TPath extends PathsWithMethod<AllPaths, 'get'>,
    TInit extends MaybeOptionalInit<AllPaths[TPath], 'get'>,
>(
    path: TPath,
    ...init: InitParam<TInit>
): MethodHandledResponse<FetchResponse<AllPaths[TPath]['get'], TInit, MediaType>> {
    return withErrorHandling(() => client.GET(path, addSentryHeaders(init[0]))) as unknown as MethodHandledResponse<FetchResponse<AllPaths[TPath]['get'], TInit, MediaType>>
}

function wrappedPut<
    TPath extends PathsWithMethod<AllPaths, 'put'>,
    TInit extends MaybeOptionalInit<AllPaths[TPath], 'put'>,
>(
    path: TPath,
    ...init: InitParam<TInit>
): MethodHandledResponse<FetchResponse<AllPaths[TPath]['put'], TInit, MediaType>> {
    return withErrorHandling(() => client.PUT(path, addSentryHeaders(init[0]))) as unknown as MethodHandledResponse<FetchResponse<AllPaths[TPath]['put'], TInit, MediaType>>
}

function wrappedPost<
    TPath extends PathsWithMethod<AllPaths, 'post'>,
    TInit extends MaybeOptionalInit<AllPaths[TPath], 'post'>,
>(
    path: TPath,
    ...init: InitParam<TInit>
): MethodHandledResponse<FetchResponse<AllPaths[TPath]['post'], TInit, MediaType>> {
    return withErrorHandling(() => client.POST(path, addSentryHeaders(init[0]))) as unknown as MethodHandledResponse<FetchResponse<AllPaths[TPath]['post'], TInit, MediaType>>
}

function wrappedPatch<
    TPath extends PathsWithMethod<AllPaths, 'patch'>,
    TInit extends MaybeOptionalInit<AllPaths[TPath], 'patch'>,
>(
    path: TPath,
    ...init: InitParam<TInit>
): MethodHandledResponse<FetchResponse<AllPaths[TPath]['patch'], TInit, MediaType>> {
    return withErrorHandling(() => client.PATCH(path, addSentryHeaders(init[0]))) as unknown as MethodHandledResponse<FetchResponse<AllPaths[TPath]['patch'], TInit, MediaType>>
}

function wrappedDelete<
    TPath extends PathsWithMethod<AllPaths, 'delete'>,
    TInit extends MaybeOptionalInit<AllPaths[TPath], 'delete'>,
>(
    path: TPath,
    ...init: InitParam<TInit>
): MethodHandledResponse<FetchResponse<AllPaths[TPath]['delete'], TInit, MediaType>> {
    return withErrorHandling(() => client.DELETE(path, addSentryHeaders(init[0]))) as unknown as MethodHandledResponse<FetchResponse<AllPaths[TPath]['delete'], TInit, MediaType>>
}

function wrappedHead<
    TPath extends PathsWithMethod<AllPaths, 'head'>,
    TInit extends MaybeOptionalInit<AllPaths[TPath], 'head'>,
>(
    path: TPath,
    ...init: InitParam<TInit>
): MethodHandledResponse<FetchResponse<AllPaths[TPath]['head'], TInit, MediaType>> {
    return withErrorHandling(() => client.HEAD(path, addSentryHeaders(init[0]))) as unknown as MethodHandledResponse<FetchResponse<AllPaths[TPath]['head'], TInit, MediaType>>
}

export const api = {
    get: wrappedGet,
    put: wrappedPut,
    post: wrappedPost,
    patch: wrappedPatch,
    delete: wrappedDelete,
    head: wrappedHead,
}
